// Deal-summary helpers for the Offers tab (O-3702). Shared by V2 and V3 offers.
//
// V2 offers carry the deal (guarantee/backend) on their budget — the editor reads
// `offer.budget`, and the offer's own dollar_amount/upside_percentage columns are
// often blank. V3 offers carry the deal on the offer itself and have no `budget`
// here. So prefer a populated budget value, falling back to the offer's column.
const dealAmount = (budgetVal, offerVal) => {
  const fromBudget = parseFloat(budgetVal) || 0;
  return fromBudget > 0 ? fromBudget : (parseFloat(offerVal) || 0);
};

const guaranteeOf = (offer) => dealAmount(offer.budget?.dollar_amount, offer.dollar_amount);
const backendOf = (offer) => dealAmount(offer.budget?.upside_percentage, offer.upside_percentage);

export const dealType = (offer) => {
  const guarantee = guaranteeOf(offer);
  const backend = backendOf(offer);
  if (guarantee > 0 && backend > 0) return 'Guarantee + Backend';
  if (guarantee > 0) return 'Flat Guarantee';
  if (backend > 0) return 'Door Deal';
  return '—';
};

export const guaranteeText = (offer) => {
  const guarantee = guaranteeOf(offer);
  if (guarantee <= 0) return '—';
  return guarantee.toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
  });
};

const REVENUE_BASE_SUFFIX = {
  gross: 'of gross ticket sales',
  net_gross: 'of net ticket sales',
  adjusted_gross: 'of ticket sales, minus expenses deducted before split',
};

const hasSplitPoint = (offer) => (parseFloat(offer.breakeven_threshold) || 0) > 0;

const backendSuffix = (offer) =>
  (offer.deal_terms_language && offer.deal_terms_language.trim())
  || REVENUE_BASE_SUFFIX[offer.revenue_base]
  || (hasSplitPoint(offer)
    ? 'of ticket sales above the split point'
    : 'of ticket sales, minus mutually agreed upon expenses');

export const backendText = (offer) => {
  const backend = backendOf(offer);
  if (backend <= 0) return '—';
  return `${backend}% ${backendSuffix(offer)}`;
};

export const getV3OfferName = (offer) =>
  offer.performer?.artistable?.name || offer.event_name || 'Untitled';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

// Created-date label. human_created_at carries the full date; fall back to the
// raw timestamp for V3 offers that don't serialize it.
export const offerDate = (offer) => offer.human_created_at || formatDate(offer.created_at) || '';
